import React, {useCallback, useEffect, useRef, useState} from 'react';
import cn from 'classnames';

import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {getCoords} from 'coreUtils/dom';
import {onVerticalScrollEnd} from 'coreUtils/utils';

import {useTabsCtx} from 'brandComponents/Tabs';

import {getTabsFixedHeight} from '../../helpers';

import styles from './BrandsNavigator.module.scss';

const allCharsList = '#abcdefghijklmnopqrstuvwxyz'.split('');

const BrandsNavigator = () => {
	const tabsCtx = useTabsCtx(true);
	const [charsList, setCharsList] = useState<string[]>([]);
	const [activeCharGroup, setActiveCharGroup] = useState<string>('');
	const isVerticalScrollActive = useRef<boolean>(false);

	useEffect(() => {
		if (!activeCharGroup && charsList.length) {
			setActiveCharGroup(charsList[0]);
		}
	}, [charsList]);

	useEffect(() => {
		if (tabsCtx?.sideComponentData.has(tabsCtx.activeTabId)) {
			setCharsList(tabsCtx.sideComponentData.get(tabsCtx.activeTabId));
		}
	}, [tabsCtx?.sideComponentData, tabsCtx?.activeTabId]);

	const handleScrollTo = useCallback(
		(char: string) => () => {
			if (!tabsCtx) {
				return;
			}

			const charGroupElement = document.querySelector(
				`#tabpane-${tabsCtx.activeTabId} ul[data-character="${char}"`,
			);

			if (charGroupElement) {
				isVerticalScrollActive.current = true;

				const coordinates = getCoords(charGroupElement as HTMLElement);
				const shouldTopOffsetBeApplied = coordinates.top > window.pageYOffset;

				if (shouldTopOffsetBeApplied) {
					const tabsHeight = getTabsFixedHeight(ViewportMgr.query('>small'));
					const processedCoordinates = coordinates.top - tabsHeight;
					ViewportMgr.scrollTo(processedCoordinates, 0, 'smooth');
				} else {
					charGroupElement.scrollIntoView({behavior: 'smooth'});
				}

				onVerticalScrollEnd().then(() => {
					isVerticalScrollActive.current = false;
					setActiveCharGroup(char);
				});
			}
		},
		[tabsCtx?.activeTabId],
	);

	useEffect(() => {
		if (isVerticalScrollActive.current || !tabsCtx?.activeBrandCharGroup) {
			return;
		}

		setActiveCharGroup(tabsCtx.activeBrandCharGroup);
	}, [tabsCtx?.activeBrandCharGroup]);

	// This is just implemented for the tabs area
	if (typeof tabsCtx === 'undefined') {
		return <></>;
	}

	return charsList.length ? (
		<ul className={styles['brands-navigator']}>
			{allCharsList.map((char) => {
				const isDisabled = !charsList.includes(char);

				return (
					<li
						key={`brands-navigator-${char}`}
						className={cn(styles['brands-navigator__item'], {
							[styles['brands-navigator__item--active']]: !isDisabled,
							[styles['brands-navigator__active-char-group']]: char === activeCharGroup
						})}
					>
						<button
							type="button"
							className={styles['brands-navigator__btn']}
							onClick={handleScrollTo(char)}
						>
							<span className={styles['brands-navigator__btn-content']}>{char}</span>
						</button>
					</li>
				);
			})}
		</ul>
	) : (
		<></>
	);
};

export default BrandsNavigator;
